import { App, Descriptions, Divider, Drawer, Image, List, Spin, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { REPORT_STATUS_LABELS } from '@/constants/report';
import { ReportProjectWorkItemsDisplay } from '@/components/report/ReportProjectWorkItemsDisplay';
import { ApiError } from '@/services/api/client';
import * as commentsApi from '@/services/api/comments';
import * as reportsApi from '@/services/api/reports';
import * as uploadsApi from '@/services/api/uploads';
import { useAuthStore } from '@/stores/auth.store';
import type { ReportComment, ReportRecord } from '@/types/report';
import { COMMENT_READ_STATUS_LABELS } from '@/utils/report-comment';

interface ReportHistoryDetailDrawerProps {
  open: boolean;
  reportId: string | null;
  onClose: () => void;
  onReadStatusChanged?: () => void;
  width?: number | string;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  height?: number | string;
  /** 移动底栏抽屉时使用移动样式展示项目进展 */
  isMobile?: boolean;
}

function resolveLeaderLabel(authorId: string, leaderId: string | null): string {
  if (leaderId && authorId === leaderId) {
    return '直属领导';
  }
  return `领导（ID: ${authorId}）`;
}

export function ReportHistoryDetailDrawer({
  open,
  reportId,
  onClose,
  onReadStatusChanged,
  width = 640,
  placement = 'right',
  height,
  isMobile = false,
}: ReportHistoryDetailDrawerProps) {
  const { message } = App.useApp();
  const currentUser = useAuthStore((state) => state.user);

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ReportRecord | null>(null);
  const [comments, setComments] = useState<ReportComment[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  const leaderLabelByAuthorId = useMemo(() => {
    const map = new Map<string, string>();
    for (const comment of comments) {
      if (!map.has(comment.authorId)) {
        map.set(
          comment.authorId,
          resolveLeaderLabel(comment.authorId, currentUser?.leaderId ?? null),
        );
      }
    }
    return map;
  }, [comments, currentUser?.leaderId]);

  const markUnreadCommentsRead = useCallback(
    async (commentList: ReportComment[]) => {
      if (!currentUser) {
        return;
      }

      const unreadForMe = commentList.filter(
        (comment) =>
          comment.receiverId === currentUser.id && comment.readStatus === 'UNREAD',
      );

      if (unreadForMe.length === 0) {
        return;
      }

      const results = await Promise.allSettled(
        unreadForMe.map((comment) => commentsApi.markCommentRead(comment.id)),
      );

      const failed = results.filter((result) => result.status === 'rejected').length;
      if (failed > 0 && failed === unreadForMe.length) {
        message.warning('标记点评已读失败，请稍后重试');
        return;
      }

      if (failed === 0) {
        onReadStatusChanged?.();
      }
    },
    [currentUser, message, onReadStatusChanged],
  );

  const loadDetail = useCallback(async () => {
    if (!reportId) {
      return;
    }

    setLoading(true);
    setReport(null);
    setComments([]);
    setImageUrls({});

    try {
      const [detail, commentList] = await Promise.all([
        reportsApi.fetchReport(reportId),
        commentsApi.fetchCommentsByReport(reportId),
      ]);

      const mergedComments = commentList.length > 0 ? commentList : detail.comments;
      setReport(detail);
      setComments(mergedComments);

      await markUnreadCommentsRead(mergedComments);

      const urlEntries = await Promise.all(
        detail.images.map(async (image) => {
          try {
            const { url } = await uploadsApi.fetchReportImageAccessUrl(image.id);
            return [image.id, url] as const;
          } catch {
            return [image.id, ''] as const;
          }
        }),
      );
      setImageUrls(Object.fromEntries(urlEntries));
    } catch (error) {
      const text = error instanceof ApiError ? error.message : '加载日报详情失败';
      message.error(text);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [markUnreadCommentsRead, message, reportId]);

  useEffect(() => {
    if (!open || !reportId) {
      return;
    }
    void loadDetail();
  }, [loadDetail, open, reportId]);

  return (
    <Drawer
      title="历史日报详情"
      open={open}
      onClose={onClose}
      width={width}
      height={height}
      placement={placement}
      destroyOnClose
      styles={placement === 'bottom' ? { body: { paddingBottom: 24 } } : undefined}
    >
      <Spin spinning={loading}>
        {report ? (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="日报日期">
                {new Date(report.reportDate).toLocaleDateString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag>{REPORT_STATUS_LABELS[report.status]}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="提交时间">
                {new Date(report.createdAt).toLocaleString('zh-CN')}
              </Descriptions.Item>
              <Descriptions.Item label="今日工作">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {report.todayWork}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="明日计划">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {report.tomorrowPlan || '—'}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="问题与风险">
                <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {report.problemRemark || '—'}
                </Typography.Paragraph>
              </Descriptions.Item>
            </Descriptions>

            <ReportProjectWorkItemsDisplay report={report} isMobile={isMobile} />

            {report.images.length > 0 ? (
              <>
                <Divider orientation="left" plain>
                  图片附件（{report.images.length}）
                </Divider>
                <Image.PreviewGroup>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {report.images.map((image) => (
                      <Image
                        key={image.id}
                        width={120}
                        height={120}
                        style={{ objectFit: 'cover' }}
                        src={imageUrls[image.id] || undefined}
                        alt={image.originalName}
                        fallback="加载失败"
                      />
                    ))}
                  </div>
                </Image.PreviewGroup>
              </>
            ) : null}

            <Divider orientation="left" plain>
              领导点评（{comments.length}）
            </Divider>
            {comments.length > 0 ? (
              <List
                size="small"
                dataSource={comments}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <SpaceBetween>
                          <span>{leaderLabelByAuthorId.get(item.authorId) ?? '领导'}</span>
                          <Tag color={item.readStatus === 'READ' ? 'default' : 'blue'}>
                            {COMMENT_READ_STATUS_LABELS[item.readStatus]}
                          </Tag>
                        </SpaceBetween>
                      }
                      description={
                        <>
                          <div>{new Date(item.createdAt).toLocaleString('zh-CN')}</div>
                          <Typography.Paragraph
                            style={{ marginTop: 8, marginBottom: 0, whiteSpace: 'pre-wrap' }}
                          >
                            {item.content}
                          </Typography.Paragraph>
                        </>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Typography.Text type="secondary">暂无领导点评</Typography.Text>
            )}
          </>
        ) : (
          !loading && <Typography.Text type="secondary">暂无数据</Typography.Text>
        )}
      </Spin>
    </Drawer>
  );
}

function SpaceBetween({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>{children}</div>
  );
}
